import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { format } from "date-fns";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useAreaList,
  useBillCycleDetails,
  useBillCycleList,
  useBillTemplates,
  useBulkBillData,
  useCategoryConfigMap,
  useConsumerCount,
  usePremiseList,
  useSubAreaList,
  useSubCategoryConfigMap,
  useSubmitConsumerSelection,
  useUpdateConsumerSelection,
  useUtilityServices,
} from "../../hooks";
import { useActiveUtilityServices } from "@shared/hooks/useActiveUtililyServices";
import { HoverRevealText } from "@shared/components/HoverRevealText";

interface TemplateOption {
  value: number;
  label: string;
  source: string;
  type?: string;
}
interface BillCycleStepProps {
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const BillCycleStep: React.FC<BillCycleStepProps> = ({
  onNext,
  onPrevious,
  currentStepIndex = 0,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
}) => {
  const [selectedBillCycleId, setSelectedBillCycleId] = useState<string>("");
  const { id, action = "add" } = useParams();

  const navigate = useNavigate();

  const { data: bulkBillData } = useBulkBillData(id);
  const { data: billCyclesList, isPending: billCycleListLoading, refetch, } =
    useBillCycleList();
  const { data: utilityServices = [] } = useActiveUtilityServices();
  const { data: categoryConfigMap = [] } = useCategoryConfigMap();
  const { data: areaList = [] } = useAreaList();
  const params = useParams();

  const { data: billCycleDetails, isLoading: isBillCycleDetailsLoading } =
    useBillCycleDetails(selectedBillCycleId);

  const [templateError, setTemplateError] = useState("");
  const [template, setTemplate] = useState<TemplateOption | null>(null);
  const dataLoadedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const billCycleId = searchParams.get("billCycleId");

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  const billDraftId = getStepData()?.billDraftId || id;

  const getExistingBillCycleId = useCallback(() => {
    if (!stepHelpers) return null;

    const allStepData = stepHelpers.getAllStepData();

    for (const [stepIndex, data] of Object.entries(allStepData)) {
      if (data?.billCycleId || data?.selectedBillCycleId) {
        return data.billCycleId || data.selectedBillCycleId;
      }
    }

    return null;
  }, [stepHelpers]);

  // Create form with only date fields
  const formFields: FormField[] = [
    {
      name: "billingPeriodStart",
      label: "Billing Period Start",
      type: "datepicker",
      required: true,
      placeholder: "Select start date",
      className: "col-span-1",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "billingPeriodEnd",
      label: "Billing Period End",
      type: "datepicker",
      required: true,
      placeholder: "Select end date",
      className: "col-span-1",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];
useEffect(() => {
  refetch(); 
}, []);
  useEffect(() => {
    if (params?.action == "edit") {
      handleBillCycleSelection(params?.id);
    }
  }, [navigate]);

  useEffect(() => {
    if (params?.action == "draft" && bulkBillData) {
      handleBillCycleSelection(bulkBillData?.billScheduleData?.id);
      billCycleForm.setValue(
        "billingPeriodStart",
        dayjs(bulkBillData.billScheduleData?.invoiceStartDate).format(
          "YYYY-MM-DD"
        )
      );
      billCycleForm.setValue(
        "billingPeriodEnd",
        dayjs(bulkBillData.billScheduleData?.invoiceEndDate).format(
          "YYYY-MM-DD"
        )
      );
      const parse = JSON.stringify({
        value: bulkBillData?.billConfigId,
        label: bulkBillData?.billScheduleData?.billCycleName,
        source: bulkBillData?.billScheduleData?.billConfigType,
      });
      setTemplate(JSON.parse(parse));
    }
  }, [navigate, bulkBillData]);

  const billCycleForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const watchedValues = billCycleForm.watch();

  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          FormService.populateForm(billCycleForm, savedStepData, formFields);

          if (savedStepData.selectedBillCycleId) {
            setSelectedBillCycleId(savedStepData.selectedBillCycleId);
          }
        } else {
          const existingBillCycleId = getExistingBillCycleId();
          if (existingBillCycleId) {
            setSelectedBillCycleId(existingBillCycleId);
          }
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
      }
    }
  }, [stepHelpers, getStepData, getExistingBillCycleId, billCycleForm]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const currentData = getStepData() || {};
    const updatedData = {
      ...currentData,
      [fieldName]: value,
      lastUpdated: new Date().toISOString(),
    };
    saveStepData(updatedData);
  };

  // Get sub area list based on billCycleDetails
  const { data: subAreaList = [] } = useSubAreaList(
    billCycleDetails?.areas || []
  );

  // Get premise list based on billCycleDetails
  const { data: premiseList = [] } = usePremiseList(
    billCycleDetails?.subAreas || []
  );

  // Get sub category list based on billCycleDetails
  const { data: subCategoryList = [] } = useSubCategoryConfigMap(
    billCycleDetails?.consumerCategories || []
  );

  const { mutateAsync: submitConsumer, isPending: isSubmitting } =
    useSubmitConsumerSelection();

  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(getStepData()?.draftId || id);

  const { data: billTemplates } = useBillTemplates();

  const categoryLabels = billCycleDetails?.consumerCategories || [];

  const { data: consumerCount } = useConsumerCount(
    billCycleDetails?.premise || [],
    categoryLabels
  );

  // Handle bill cycle selection
  const handleBillCycleSelection = (cycleId: string) => {
    if (cycleId) {
      setSelectedBillCycleId(cycleId);

      saveStepData({
        selectedBillCycleId: cycleId,
        ...billCycleForm.getValues(),
      });
    }
  };

  useEffect(() => {
    if (billCycleId) {
      handleBillCycleSelection(billCycleId);
    }
  }, [navigate]);

  // Handle create new cycle - navigate to add page
  const handleCreateNew = () => {
    navigate("/bx/bill-setup/billcycle/action/add");
  };

  // Handle form submission
  const handleFormSubmit = async (data: any) => {
    try {
      if (clearValidationError) {
        clearValidationError();
      }

      // Validate that a cycle is selected
      if (!selectedBillCycleId) {
        if (setValidationError) {
          setValidationError("Please select a billing cycle.");
        }
        return;
      }

      const isValidForm = await FormService.validateForm(billCycleForm);
      const isTemplateSelected = !!template;

      if (!isTemplateSelected) {
        setTemplateError("Please select a bill template.");
      } else {
        setTemplateError("");
      }

      const isValid = isValidForm && isTemplateSelected;

      if (!isValid) {
        if (setValidationError) {
          setValidationError(
            "Please fill in all required fields and select a bill template."
          );
        }
        return;
      }

      let payload = FormService.createPayload(data, formFields);

      // Build payload using billCycleDetails data
      const formattedData = {
        bill_data: {
          select_cycle: selectedBillCycleId,
          bill_config_id: template.value,
          bill_config_type: template.source,
          remote_utility_id: String(getRemoteUtilityId()),
          bill_schedule_data: {
            id: selectedBillCycleId,
            bill_config_type: template.source,
            bill_cycle_name: billCycleDetails?.name || "",
            area: billCycleDetails?.areas || [],
            sub_area: billCycleDetails?.subAreas || [],
            premise: billCycleDetails?.premise || [],
            utility_services:
              billCycleDetails?.utilityServices?.map(
                (service: any) => service.name
              ) || [],
            category: billCycleDetails?.consumerCategories || [],
            sub_category: billCycleDetails?.consumerSubCategories || [],
            invoice_start_date: payload.billingPeriodStart,
            invoice_end_date: payload.billingPeriodEnd,
            consumer_count: consumerCount,
          },
        },
        step: {
          step_working_on: 0,
          total_steps: 5,
        },
      };

      let response;
      if (getStepData()?.draftId || id) {
        response = await updateConsumer(formattedData);
      } else {
        response = await submitConsumer(formattedData);
      }

      if (response) {
        const draftId = response?.billDraftId;

        saveStepData({
          ...data,
          template,
          selectedBillCycleId,
          draftId: draftId,
          formattedData,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        if (stepHelpers) {
          stepHelpers.setDataToStep(
            currentStepIndex,
            "billCycleId",
            selectedBillCycleId
          );
          stepHelpers.setDataToStep(
            0,
            "workflowBillCycleId",
            selectedBillCycleId
          );
        }

        if (onNext) {
          onNext();
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (setValidationError) {
        setValidationError("Failed to submit form. Please try again.");
      }
    }
  };

  const handleDraft = async (data: any) => {
    try {
      if (clearValidationError) {
        clearValidationError();
      }

      if (!selectedBillCycleId) {
        if (setValidationError) {
          setValidationError("Please select a billing cycle.");
        }
        return;
      }

      const isValidForm = await FormService.validateForm(billCycleForm);
      const isTemplateSelected = !!template;

      if (!isTemplateSelected) {
        setTemplateError("Please select a bill template.");
      } else {
        setTemplateError("");
      }

      const isValid = isValidForm && isTemplateSelected;

      if (!isValid) {
        if (setValidationError) {
          setValidationError(
            "Please fill in all required fields and select a bill template."
          );
        }
        return;
      }

      let payload = FormService.createPayload(data, formFields);

      const formattedData = {
        bill_data: {
          select_cycle: selectedBillCycleId,
          bill_config_id: template.value,
          remote_utility_id: String(getRemoteUtilityId()),
          bill_schedule_data: {
            id: selectedBillCycleId,
            bill_config_type: template.source,
            bill_cycle_name: billCycleDetails?.name || "",
            area: billCycleDetails?.areas || [],
            sub_area: billCycleDetails?.subAreas || [],
            premise: billCycleDetails?.premise || [],
            utility_services:
              billCycleDetails?.utilityServices?.map(
                (service: any) => service.name
              ) || [],
            category: billCycleDetails?.consumerCategories || [],
            sub_category: billCycleDetails?.consumerSubCategories || [],
            invoice_start_date: payload.billingPeriodStart,
            invoice_end_date: payload.billingPeriodEnd,
            consumer_count: consumerCount,
          },
        },
        step: {
          step_working_on: 0,
          total_steps: 5,
        },
      };

      let response;
      if (getStepData()?.draftId || id) {
        response = await updateConsumer(formattedData);
      } else {
        response = await submitConsumer(formattedData);
      }

      if (response) {
        const draftId = response?.billDraftId;

        saveStepData({
          ...data,
          template,
          selectedBillCycleId,
          draftId: draftId,
          formattedData,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        if (stepHelpers) {
          stepHelpers.setDataToStep(
            currentStepIndex,
            "billCycleId",
            selectedBillCycleId
          );
          stepHelpers.setDataToStep(
            0,
            "workflowBillCycleId",
            selectedBillCycleId
          );
        }

        if (response) {
          navigate("/bx/history?tab=bulk&tab1=draft");
          await queryClient.refetchQueries({
            queryKey: [
              QueryKeyFactory.module.bx.billHistory.BulkBillDraft(),
              1,
            ],
            exact: false,
          });
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (setValidationError) {
        setValidationError("Failed to submit form. Please try again.");
      }
    }
  };

  const renderBillingCycleSummary = () => {
    const formValues = billCycleForm.getValues();

    const getNames = (ids: string[], list: any[]) =>
      ids
        ?.map((id) => list.find((item: any) => item.id === id)?.name)
        .filter(Boolean)
        .join(", ") || "—";

    return (
      <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102 focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-accent">
        <CardHeader className="pb-3">
          <CardTitle>Billing Cycle Summary</CardTitle>
          <CardDescription>
            Detailed overview of the selected billing cycle configuration.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              {/* Cycle Name */}
              <div>
                <span className="text-sm text-muted-foreground">
                  Cycle Name
                </span>
                <div className="font-medium">
                  {billCycleDetails?.name || "—"}
                </div>
              </div>

              {/* Areas */}
              <div>
                <span className="text-sm text-muted-foreground">Areas</span>
                <HoverRevealText
                  text={getNames(billCycleDetails?.areas || [], areaList)}
                />
              </div>

              {/* Sub Areas */}
              <div>
                <span className="text-sm text-muted-foreground">Sub Areas</span>
                <HoverRevealText
                  text={getNames(billCycleDetails?.subAreas || [], subAreaList)}
                />
              </div>

              {/* Premises */}
              <div>
                <span className="text-sm text-muted-foreground">Premises</span>
                <HoverRevealText
                  text={getNames(billCycleDetails?.premise || [], premiseList)}
                />
              </div>

              {/* Utility Services */}
              <div>
                <span className="text-sm text-muted-foreground">
                  Utility Services
                </span>
                <HoverRevealText
                  text={
                    billCycleDetails?.utilityServices
                      ?.map((s: any) => s.name)
                      .join(", ") || "—"
                  }
                />
              </div>

              {/* Consumer Categories */}
              <div>
                <span className="text-sm text-muted-foreground">
                  Consumer Categories
                </span>
                <HoverRevealText
                  text={getNames(
                    billCycleDetails?.consumerCategories || [],
                    categoryConfigMap
                  )}
                />
              </div>

              {/* Premises Count */}
              <div>
                <span className="text-sm text-muted-foreground">
                  Premises Count
                </span>
                <div>{billCycleDetails?.premise?.length || 0} selected</div>
              </div>

              {/* Billing Period */}
              <div>
                <span className="text-sm text-muted-foreground">
                  Billing Period onkar
                </span>
                <div>
                  {formValues.billingPeriodStart && formValues.billingPeriodEnd
                    ? `${formValues.billingPeriodStart} to ${formValues.billingPeriodEnd}`
                    : "—"}
                </div>
              </div>
            </div>

            {/* Bill Template */}
            <span className="text-sm text-muted-foreground">Bill Template</span>
            <Select
              value={template ? JSON.stringify(template) : ""}
              onValueChange={(value) => {
                setTemplate(JSON.parse(value));
                setTemplateError("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Bill Template" />
              </SelectTrigger>
              <SelectContent>
                {billTemplates?.map((option: any) => (
                  <SelectItem key={option.value} value={JSON.stringify(option)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {templateError && (
              <p className="text-sm text-red-500 mt-1">{templateError}</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="page-subheading">Bill Cycle Configuration</h3>
        <p className="page-subtitle">
          Select billing cycle and configure dates.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102">
            <CardHeader className="pb-3">
              <CardTitle>Cycle Selection</CardTitle>
              <CardDescription>
                Select an existing billing cycle or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {isBillCycleDetailsLoading
                      ? "loading data..."
                      : "Select Billing Cycle"}
                  </label>
                  <select
                    value={selectedBillCycleId}
                    onChange={(e) => handleBillCycleSelection(e.target.value)}
                    className="w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 h-10 px-3"
                  >
                    <option value="">
                      {billCycleListLoading
                        ? "loading billing cycle..."
                        : "Select a billing cycle"}
                    </option>
                    {billCyclesList?.map((cycle: any) => (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleCreateNew}
                  >
                    Create New
                  </Button>
                  {selectedBillCycleId && (
                    <div className="flex flex-col space-y-3">
                      <div className="text-sm font-medium text-gray-600 mt-4">
                        Cycle Comparison
                      </div>
                      <div className="flex justify-between items-start text-sm text-gray-600">
                        <div>
                          <div className="mb-1">Previous Period</div>
                          <div className="text-gray-900">
                            {billCycleDetails?.invoiceStartDate ?? "NA"} to{" "}
                            {billCycleDetails?.invoiceEndDate ?? "NA"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="mb-1">Days Since Last Billing</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {billCycleDetails?.daysSinceLastReading ?? "NA"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {selectedBillCycleId && (
            <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102">
              <CardHeader className="pb-3">
                <CardTitle>Billing Period</CardTitle>
                <CardDescription>
                  Configure billing period dates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DynamicForm
                  fields={formFields}
                  form={billCycleForm}
                  onSubmit={handleFormSubmit}
                  onFieldChange={handleFieldChange}
                  gridColumns={2}
                  responsiveColumns={{ sm: 1, md: 2 }}
                  config={{
                    layout: "vertical",
                    spacing: "small",
                    showSubmitButton: false,
                    showResetButton: false,
                    classes: {
                      form: "space-y-4",
                      fieldContainer: "w-full",
                    },
                  }}
                />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          {selectedBillCycleId && renderBillingCycleSummary()}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button
          onClick={() => handleDraft(billCycleForm.getValues())}
          disabled={!selectedBillCycleId || isSubmitting || isUpdating}
        >
          {isSubmitting || isUpdating ? (
            <div className="flex items-center">
              <span className="mr-2">Saving...</span>
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
            </div>
          ) : (
            "Save As Draft"
          )}
        </Button>
        <Button
          onClick={() => handleFormSubmit(billCycleForm.getValues())}
          disabled={!selectedBillCycleId || isSubmitting || isUpdating}
        >
          {isSubmitting || isUpdating ? (
            <div className="flex items-center">
              <span className="mr-2">Processing</span>
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
            </div>
          ) : (
            "Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default BillCycleStep;
